<template>
	<div class="vertical-panel">
		<div v-if="showHeader" class="vertical-panel-header">
			<slot name="header" />
		</div>
		<div v-if="showSubHeader" class="vertical-panel-sub-header">
			<slot name="sub-header" />
		</div>
		<div ref="container" class="vertical-panel-content">
			<div
				v-if="showUpper"
				ref="upper"
				class="vertical-panel-upper"
				:style="upperStyle"
			>
				<slot name="upper" />
			</div>
			<div
				v-if="showLower && showLowerPanel && !disabled"
				class="resize-panel"
				@mousedown.stop.prevent="onResize($event)"
			/>
			<div
				ref="lower"
				:class="{hidden: !showLowerPanel}"
				class="vertical-panel-lower"
				:style="lowerStyle"
			>
				<transition name="slide" mode="in-out" >
					<div v-if="showLower && showLowerPanel" class="vertical-panel-lower-inner">
						<slot name="lower" />
					</div>
				</transition>
			</div>
			<div v-show="resizing" ref="preview" class="resize-preview" />
		</div>
		<slot name="dialogs" />
	</div>
</template>

<script>
export default {
	name: 'TResizableVerticalPanel',
	props: {
		hideSubHeader: {
			type: Boolean,
			default: false
		},
		showLowerPanel: {
			type: Boolean,
			default: true
		},
		upperPercentage: {
			type: Number,
			default: 50
		},
		upperHeight: { type: Number },
		minUpperPercentage: {
			type: Number,
			default: 20
		},
		minUpperHeight: { type: Number },
		minLowerPercentage: {
			type: Number,
			default: 20
		},
		minLowerHeight: { type: Number },
		localStorageId: { type: String },
		disabled: { type: Boolean }
	},
	data() {
		return {
			resizing: false,
			upperFlex: 50,
			percentage: 50,
			observer: null,
			containerHeight: 0
		};
	},
	computed: {
		showHeader() {
			return Boolean(this.$slots['header']);
		},
		showSubHeader() {
			return Boolean(this.$slots['sub-header']) && !this.hideSubHeader;
		},
		showUpper() {
			return Boolean(this.$slots['upper']);
		},
		showLower() {
			return Boolean(this.$slots['lower']);
		},
		upperFlexValue() {
			return this.disabled ? 0 : this.upperFlex;
		},
		upperStyle() {
			if (!this.showLowerPanel) return 'height: 100%';
			return `flex: ${this.upperFlexValue} 1 0%`;
		},
		lowerStyle() {
			return `flex: ${100 - this.upperFlexValue} 1 0%`;
		},
		minUpper() {
			if (this.minUpperHeight && this.containerHeight > 0) {
				return (this.minUpperHeight / this.containerHeight) * 100;
			}
			return this.minUpperPercentage;
		},
		minLower() {
			if (this.minLowerHeight && this.containerHeight > 0) {
				return (this.minLowerHeight / this.containerHeight) * 100;
			}
			return this.minLowerPercentage;
		}
	},
	watch: {
		upperPercentage() {
			this.calculateUpperFlex();
		},
		upperHeight() {
			this.calculateUpperFlex();
		},
		localStorageId(newVal, oldVal) {
			if (newVal === oldVal) return;
			else if (newVal) this.updateUpperFlexFromLocalStorage();
			else this.calculateUpperFlex();
		}
	},
	async mounted() {
		await this.$nextTick();
		let { container } = this.$refs;
		if (container) {
			this.observer = new ResizeObserver(() => {
				let { height } = container.getBoundingClientRect();
				this.containerHeight = height;
			});
			this.observer.observe(container);

			let { height } = container.getBoundingClientRect();
			this.containerHeight = height;
		}

		this.calculateUpperFlex();
		this.updateUpperFlexFromLocalStorage();
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
	},
	methods: {
		onResize(event) {
			this.startResizeColumn(event);
		},
		clampUpperFlex(value) {
			return Math.min(100 - this.minLower, Math.max(value, this.minUpper));
		},
		calculateUpperFlex() {
			this.upperFlex = this.clampUpperFlex(this.upperPercentage);
			if (this.upperHeight) {
				this.upperFlex = this.clampUpperFlex((this.upperHeight / this.containerHeight) * 100);
			}
		},
		updateUpperFlexFromLocalStorage() {
			if (this.localStorageId) {
				let percentage = localStorage.getItem(`${this.localStorageId}/verticalResizePercentage`);
				if (percentage)
					this.upperFlex = this.clampUpperFlex(percentage);
			}
		},
		startResizeColumn() {
			let bounding = this.$refs.container.getBoundingClientRect();
			this.containerHeight = bounding.height;
			this.$refs.preview.style.top=`${(this.upperFlex/100)*this.containerHeight - 102}px`;
			this.resizing = true;

			this.resizeMouseupListener = (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.finishResize();
			};
			this.resizeMousemoveListener = (e) => {
				e.preventDefault();
				e.stopPropagation();

				if ((e.buttons & 1) === 0) {
					this.finishResize();
					return;
				}
				let relative = e.pageY - bounding.top;
				this.percentage = this.clampUpperFlex((relative / this.containerHeight) * 100);
				this.$refs.preview.style.top=`${(this.percentage/100) * this.containerHeight - 102}px`;
			};
			window.addEventListener('mouseup', this.resizeMouseupListener);
			window.addEventListener('mousemove', this.resizeMousemoveListener);
		},
		finishResize() {
			setTimeout(() => this.resizing = false, 100);
			this.upperFlex = this.clampUpperFlex(this.percentage);
			if (this.localStorageId) {
				localStorage.setItem(`${this.localStorageId}/verticalResizePercentage`, this.upperFlex);
			}
			if (this.resizeMousemoveListener) {
				window.removeEventListener('mousemove', this.resizeMousemoveListener);
				this.resizeMousemoveListener = null;
			}
			if (this.resizeMouseupListener) {
				window.removeEventListener('mouseup', this.resizeMouseupListener);
				this.resizeMouseupListener = null;
			}
		},
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
.vertical-panel, .vertical-panel-content {
	height: 100%;
	display: flex;
	flex-direction: column;
	position: relative;
}
.resize-preview {
	position: absolute;
	border-top: 100px solid transparent;
	border-bottom: 100px solid transparent;
	background-clip: content-box;
	height: 4px;
	background-color: #c1c1c1;
	left: 0;
	right: 0;
	z-index: 9999;
	cursor: ns-resize;
}
.resize-panel {
	user-select: none;
	height: 9px;
	background-clip: content-box;
	width: 100%;
	box-sizing: content-box;
	cursor: ns-resize;
	flex-shrink: 0;
	box-shadow: 0px 5px 3px -3px rgba(0,0,0,0.1) inset;
}


.vertical-panel-header {
	z-index: 3;
}

.vertical-panel-sub-header {
	z-index: 2;
}

.vertical-panel-header, .vertical-panel-sub-header {
	height: 50px;
	min-height: 50px;
	max-height: 50px;
	background: white;
	box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1);
}

.vertical-panel-upper {
	z-index: 4;
}

.vertical-panel-lower {
	position: relative;
	height: auto;
	z-index: 3;
	padding-top: 3px;
	.vertical-panel-lower-inner {
		height:100%;
	}
	&.hidden {
		height: 0px;
	}
}
</style>
